/* eslint-disable @next/next/no-img-element */
import Head from "next/head";
import { useRouter } from "next/router";

import { useEffect } from "react";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { HELIXTAP_INTRODUCER_ID } from "components/Helixtap/constants";
import { Form, SIGN_UP_BENEFITS, SIGN_UP_TEXTS } from "components/SignUpForm";
import type { NextPageWithLayout } from "pages/_app";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { emberAppBaseUrl } from "utils/envVariables";

type SignupPageProps = {};

const title = "Sign Up | Smartkarma";
const description =
  "The Smartkarma Preview Pass is your entry to the Independent Investment Research Network";

const SignupPage: NextPageWithLayout = ({}: SignupPageProps) => {
  const router = useRouter();
  const media = useMediaQuery();

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  const { authenticated, account: loggedInAccount } = useAuthentication();

  useEffect(() => {
    if (authenticated) {
      if (loggedInAccount?.introducer?.id === HELIXTAP_INTRODUCER_ID) {
        window.location.href = "/helixtap/dashboard";
        return;
      }
      window.location.href = "/dashboard";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <title>{title}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content="https://assets.smartkarma.com/v3/img/smartkarma-research-reinvented-dark.png"
        />
      </Head>

      <div className="bg-gradient-120 from-active-black to-denim pt-[48px] md:pt-[60px]">
        <div className="mt-[30px] md:mt-0 md:flex md:flex-col md:items-center md:min-h-[calc(100vh-60px)]">
          <div className="md:max-w-[800px] desktop:max-w-[915px] md:flex md:items-center md:justify-center md:flex-1 md:gap-[78px] m-[30px]">
            <div className="text-white md:h-[630px]">
              <h1 className="text-[37px] md:text-[60px] font-[600] leading-[45px] md:tracking-[1.12px] tracking-[0.8px] mb-[20px]">
                {SIGN_UP_TEXTS.heading}
              </h1>
              <h2 className="text-[16px] leading-[22px] md:text-[19px] md:leading-[30px] tracking-[0.37px] font-[500]">
                {SIGN_UP_TEXTS.description}
              </h2>
              <div className="md:flex-1 mt-[20px] mb-[20px] md:mt-[30px] text-[15px] md:text-[18px] leading-[24px] md:leading-[45px] tracking-[0.36px]">
                <ul className="list-none m-0 p-0">
                  {SIGN_UP_BENEFITS.map((benefit, index) => (
                    <li key={index} className="flex items-center ">
                      <span className="rounded-[50%] bg-white text-[11px] md:text-[16px] text-karma-green w-[18px] md:w-[30px] h-[18px] md:h-[30px] flex items-center justify-center">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      <span className="ml-[10px] md:ml-[18px]">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[15px] leading-[22px] md:text-[18px] md:leading-[30px] tracking-[0.37px]">
                {SIGN_UP_TEXTS.description2}
              </p>
              <p className="text-[15px] leading-[22px] md:text-[18px] md:leading-[30px] tracking-[0.37px]">
                {SIGN_UP_TEXTS.description3}
              </p>
            </div>
            <div className="w-full md:max-w-[318px] md:relative">
              <div className="bg-white rounded-[16px] px-[24px] py-[34px] pb-[25px]">
                <Form type="insight-signup" />
              </div>
              <div className="my-[20px] text-white font-bold text-center tracking-[0.32px]">
                Already have an account?{" "}
                <span
                  className="text-white hover:text-smart-teal cursor-pointer underline"
                  onClick={() => router.push("/signin")}
                >
                  Sign In Now
                </span>
              </div>
            </div>
          </div>
          {media.mobile ? (
            <div className="px-[50px] py-[50px] left-0 w-full mx-[auto] text-center bg-white">
              <img
                style={{
                  maxWidth: "320px",
                  height: "auto",
                }}
                src="https://wp-static.smartkarma.com/../assets/uploaded/2021/02/hero-logos-2021-mobile.svg"
                alt="Smartkarma Hero Logos"
              />
            </div>
          ) : (
            <div className="bg-white px-[2%] py-[50px] left-0 w-full mx-[auto]">
              <img
                width="100%"
                height="40"
                src="https://wp-static.smartkarma.com/assets/uploaded/2021/02/smartkarma-logos-2021.svg"
                alt="Smartkarma Hero Logos"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SignupPage;
